import { useContext, useMemo } from "react";
import cx from "classnames";
import {
  Heading,
  Pill,
  RadioButton,
  RoundButton,
  Switch,
  Table,
  Text,
  TextField,
} from "@foris/avocado-suite";
import LinkRouter from "@common/components/link-router";
import { IGroup, IGroupValue } from "@modules/vacancies/contextVacancies/ContextVacancies";
import useContextUrl from "@common/hooks/useContextUrl";
import styles from "./groupsTable.module.scss";
import GroupTableCourseColumn from "../group-table-course-column";
import { ContextApp } from "@config/Context/contextApp";
import { ContextEDH } from "@context/ContextEDH";
import useGroupsInputState from "../hooks/useGroupsInputState";
import { useTranslation } from "react-i18next";
import { getColumnCell } from "@common/utils/table.util";
import { emitEvent } from "@common/hooks/useListenEvent";
import GroupActionButton from "../group-action-button";
import { IconTypes } from "@foris/avocado-icons";
import { useBundleEditAbilities } from "@modules/vacancies/hooks/useBundleEditAbilities";

interface GroupsTableProps {
  groups: IGroup[];
  edhCanEdit?: boolean;
  isEditing?: boolean;
  linkId: string;
  hasGroupSections?: boolean;
  onCreateGroup?: () => void;
  onToggleActive?: (group: IGroup, checked: boolean) => void;
  onDeleteGroup?: (group: IGroup) => void;
  onMigrateGroup?: (group: IGroup) => void;
  onToggleVisibility?: (group: IGroup) => void;
  onChangeInputValue?: (countVacancies: IGroupValue[]) => void;
  onChangeRetakerCapacity?: (values: IGroupValue[]) => void;
}

const GroupsTable = ({
  groups = [],
  edhCanEdit,
  isEditing,
  linkId,
  hasGroupSections = false,
  onToggleVisibility,
  onToggleActive,
  onCreateGroup,
  onDeleteGroup,
  onMigrateGroup,
  onChangeInputValue,
  onChangeRetakerCapacity,
}: GroupsTableProps) => {
  const context = useContext(ContextApp);
  const { state } = useContext(ContextEDH);
  const { workspace, origin, scenario } = useContextUrl();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const userCanMigrate =
    context?.user?.permissions?.update &&
    context?.user?.abilities?.can_migrate_groups_in_bundle_view;

  const userCanDelete =
    context?.user?.permissions?.delete &&
    context?.user?.abilities?.can_delete_groups_in_bundle_view;

  const {
    canEditVacancies,
    canEditRetakers,
    canSelectReferent: canSelectReferentAbility,
    showRetakersColumns,
  } = useBundleEditAbilities();

  const canAddMoreGroups = useMemo(() => {
    if (state?.base?.groupsInLinkRestriction?.enabled) {
      return groups?.length < state?.base?.groupsInLinkRestriction?.groups_allowed;
    }

    return true;
  }, [groups, state?.base?.groupsInLinkRestriction]);

  const canAddGroup =
    edhCanEdit &&
    context?.user?.permissions?.create &&
    context?.user?.abilities?.can_create_groups_in_bundle_view &&
    canAddMoreGroups;

  const {
    countVacancies,
    onChangeInput,
    retakerCapacities,
    onChangeRetakerInput,
    referentGroup,
    setNewReferentGroupId,
  } = useGroupsInputState({
    groups,
    linkId,
    isEditing,
    onChange: onChangeInputValue,
    onChangeRetaker: onChangeRetakerCapacity,
  });

  const countVacanciesById = useMemo(() => {
    return countVacancies?.reduce((acc: { [id: string]: IGroupValue }, item: IGroupValue) => {
      acc[item.group.id] = item;
      return acc;
    }, {}) as { [id: string]: IGroupValue };
  }, [countVacancies]);

  const retakerCapacitiesById = useMemo(() => {
    return retakerCapacities?.reduce((acc: { [id: string]: IGroupValue }, item: IGroupValue) => {
      acc[item.group.id] = item;
      return acc;
    }, {}) as { [id: string]: IGroupValue };
  }, [retakerCapacities]);

  const totalRetakerCapacities = useMemo(() => {
    return retakerCapacities?.reduce((acc, item) => acc + (item.value ?? 0), 0);
  }, [retakerCapacities]);

  const totalGroupsData = useMemo(() => {
    return groups?.reduce(
      (acc, item) => {
        acc.enrollment += item.enrollment;
        acc.waitList += item.waitList;
        return acc;
      },
      { enrollment: 0, waitList: 0 },
    );
  }, [groups]);

  const totalGroupsCountVacancies = useMemo(() => {
    return countVacancies?.reduce((acc, item) => acc + item.value, 0);
  }, [countVacancies]);

  const groupSectionCodesByGroupId = useMemo(() => {
    const groupSectionsWithCodeByGroupId = {};

    if (!hasGroupSections) return groupSectionsWithCodeByGroupId;

    groups?.forEach(group => {
      const groupSectionsWithCode = group?.groupSections?.filter(section => section?.code) ?? [];
      groupSectionsWithCodeByGroupId[group.id] = groupSectionsWithCode?.length
        ? groupSectionsWithCode?.map(groupSection => groupSection?.code)?.join(", ")
        : null;
    });

    return groupSectionsWithCodeByGroupId;
  }, [groups, hasGroupSections]);

  const columns = useMemo(() => {
    const initialColumns = [
      {
        key: "isActive",
        header: t("bundle.link-card.group-table.header.active"),
        renderCell: (data: any) => {
          if (data?.isFooter)
            return <Text weight="medium">{t("bundle.link-card.table-footer.total")}</Text>;

          const item = data as IGroup;
          return (
            <Switch
              checked={item.isActive}
              onChange={(checked: boolean) =>
                canEditVacancies && item?.course?.edhCanEdit && onToggleActive?.(item, checked)
              }
              disabled={!canEditVacancies || !item?.course?.edhCanEdit}
            />
          );
        },
        desktopOptions: {
          headCellStyles: {
            width: "75px",
          },
        },
      },
      {
        key: "openGroup",
        header: t("bundle.link-card.group-table.header.visible"),
        renderCell: (data: any) => {
          if (data?.isFooter) return "";

          const item = data as IGroup;
          return (
            <RoundButton
              className={styles.openGroup}
              variant="secondary"
              size="sm"
              icon={item.openGroup ? "open-eye" : "closed-eye"}
              onClick={() =>
                canEditVacancies && item?.course?.edhCanEdit && onToggleVisibility?.(item)
              }
              disabled={!canEditVacancies || !item?.course?.edhCanEdit}
            />
          );
        },
        desktopOptions: {
          headCellStyles: {
            width: "70px",
          },
        },
      },
      {
        key: "clientCode",
        header: t("bundle.link-card.group-table.header.group-client-code"),
        renderCell: (data: any) => {
          if (data?.isFooter) return "";

          const item = data as IGroup;
          const showCheckbox = state?.base?.groupsManagerType !== "CAMPUS";
          const canSelectReferent = isEditing && showCheckbox && canSelectReferentAbility;

          return (
            <div
              className={cx(
                styles.groupClientCode,
                canSelectReferent && styles.groupClientCode__isEditing,
              )}
            >
              <div className={styles.groupClientCode_content}>
                {canSelectReferent && (
                  <RadioButton
                    className={styles.groupClientCode_radio}
                    checked={referentGroup === item.id}
                    onChange={() => {
                      emitEvent("on:update", {
                        target: "group-referent",
                        data: {
                          linkId: linkId,
                          groupId: item.id,
                          isReferent: true,
                        },
                      });
                      setNewReferentGroupId(item.id);
                    }}
                  />
                )}
                <LinkRouter
                  to={`/scheduler/editor/group/${workspace}/${scenario}/${origin}/${item.id}`}
                  className={styles.link}
                  colored
                >
                  {item.label}{" "}
                  {item.clientCode && (
                    <>
                      <span>|</span> <span className={styles.link_regular}>{item.clientCode}</span>
                    </>
                  )}
                </LinkRouter>
              </div>

              {referentGroup === item?.id && (
                <Pill className={styles.groupClientCode_pill}>
                  {t("groups-manager.groups-table.table.headers.main-group")}
                </Pill>
              )}
            </div>
          );
        },

        desktopOptions: {
          headCellStyles: {
            width: "160px",
            minWidth: "80px",
          },
        },
      },
      {
        key: "course",
        header: t("bundle.link-card.group-table.header.subject-key"),
        renderCell: (data: any) => {
          if (data?.isFooter) return "";

          const group = data as IGroup;

          const props = {
            groupLabel: `${group?.course?.code} - ${group?.course?.name}`,
            termCode: group?.term?.code,
            campusCode: group?.course?.curriculum?.program?.campus?.code,
            departmentCode: group?.course?.curriculum?.program?.department?.code,
            modalityCode: group?.course?.curriculum?.program?.modality?.code,
            shiftCode: group?.course?.curriculum?.program?.shift?.code,
            programCode: group?.course?.curriculum?.program?.code,
            curriculumCode: group?.course?.curriculum?.code,
          };

          return <GroupTableCourseColumn hasOnboarding={false} {...props} />;
        },
        desktopOptions: {
          headCellStyles: {
            width: "250px",
            minWidth: "80px",
          },
        },
      },
      {
        key: "vacancies",
        header: t("bundle.link-card.group-table.header.vacancies"),
        renderCell: (data: any) => {
          if (data?.isFooter) return <Text weight="medium">{totalGroupsCountVacancies}</Text>;

          const item = data as IGroup;
          return (
            <TextField
              className={styles.groupVacanciesInput}
              type="number"
              value={countVacanciesById?.[item?.id]?.value}
              onChange={e => onChangeInput?.(e.target.value, item?.id)}
              disabled={!isEditing || !canEditVacancies}
            />
          );
        },
        desktopOptions: {
          headCellStyles: {
            width: "90px",
            minWidth: "80px",
          },
        },
      },
      {
        key: "enrollment",
        header: t("bundle.link-card.group-table.header.enrollments"),
        renderCell: (data: any) => {
          if (data?.isFooter) return <Text weight="medium">{totalGroupsData.enrollment}</Text>;

          const item = data as IGroup;
          return <Text>{item.enrollment ?? 0}</Text>;
        },
        desktopOptions: {
          headCellStyles: {
            width: "90px",
            minWidth: "80px",
          },
        },
      },
      {
        key: "waitList",
        header: t("bundle.link-card.group-table.header.waiting-list"),
        renderCell: (data: any) => {
          if (data?.isFooter) return <Text weight="medium">{totalGroupsData.waitList}</Text>;

          const item = data as IGroup;
          return <Text>{item.waitList ?? 0}</Text>;
        },
        desktopOptions: {
          headCellStyles: {
            width: "130px",
            minWidth: "80px",
          },
        },
      },
    ];

    if (showRetakersColumns) {
      initialColumns.push(
        {
          key: "retakerCapacity",
          header: t("bundle.link-card.group-table.header.retaker-capacity"),
          renderCell: (data: any) => {
            if (data?.isFooter) return <Text weight="medium">{totalRetakerCapacities}</Text>;

            const item = data as IGroup;

            if (isEditing && canEditRetakers) {
              return (
                <TextField
                  className={styles.groupVacanciesInput}
                  type="number"
                  value={retakerCapacitiesById?.[item?.id]?.value}
                  onChange={e => onChangeRetakerInput?.(e.target.value, item?.id)}
                />
              );
            }

            return <Text>{retakerCapacitiesById?.[item?.id]?.value ?? 0}</Text>;
          },
          desktopOptions: {
            headCellStyles: {
              width: "90px",
              minWidth: "80px",
            },
          },
        },
        {
          key: "retakerEnrollments",
          header: t("bundle.link-card.group-table.header.retaker-enrollments"),
          renderCell: (data: any) => {
            if (data?.isFooter) return "";

            const item = data as IGroup;
            return <Text>{item.enrollmentStats?.retakerEnrollmentsCount ?? 0}</Text>;
          },
          desktopOptions: {
            headCellStyles: {
              width: "130px",
              minWidth: "80px",
            },
          },
        },
      );
    }

    if (hasGroupSections) {
      initialColumns.splice(3, 0, {
        key: "groupSections",
        header: t("bundle.link-card.group-table.header.group-section"),
        renderCell: (data: any) => {
          if (data?.isFooter) return "";

          const item = data as IGroup;
          return getColumnCell(groupSectionCodesByGroupId?.[item.id] ?? "-");
        },

        desktopOptions: {
          headCellStyles: {
            width: "130px",
            minWidth: "80px",
          },
        },
      });
    }

    return initialColumns;
  }, [
    workspace,
    origin,
    scenario,
    totalGroupsData,
    groups,
    isEditing,
    countVacanciesById,
    totalGroupsCountVacancies,
    language,
    groupSectionCodesByGroupId,
    hasGroupSections,
    referentGroup,
    retakerCapacitiesById,
    totalRetakerCapacities,
    canEditRetakers,
    canEditVacancies,
    canSelectReferentAbility,
    showRetakersColumns,
  ]);

  return (
    <div className={styles.groupsTable}>
      <Heading type="h4" className={styles.groupsTable_title}>
        {t("bundle.link-card.group-table.title")}
      </Heading>

      <Table
        data={[
          ...(groups ?? []),
          {
            isFooter: true,
          },
        ]}
        columns={columns}
        rightActions={[
          {
            renderContent(row: any) {
              if (row?.isFooter) {
                return (
                  canAddGroup && (
                    <div className={styles.actions}>
                      <RoundButton
                        variant="ghost"
                        size="sm"
                        icon="circle-plus"
                        className={styles.action}
                        onClick={() => onCreateGroup?.()}
                      />
                    </div>
                  )
                );
              }

              const group = row as IGroup;
              const hasRestrictionsSettings = state?.base?.restrictionConfigurations?.enabled;

              const menuItems: {
                label: string;
                icon?: IconTypes;
                onClick: () => void;
                disabled?: boolean;
              }[] = [
                {
                  label: context?.user?.permissions?.update
                    ? t("bundle.link-card.group-table.actions.group-tags")
                    : t("bundle.link-card.group-table.actions.view-group-tags"),
                  onClick: () => {
                    emitEvent("on:update", {
                      target: "open-group-tags-drawer",
                      data: { groupId: group?.id, group },
                    });
                  },
                },
              ];

              if (hasRestrictionsSettings) {
                menuItems.push({
                  label: t("bundle.link-card.group-table.actions.group-restrictions"),
                  icon: "lock",
                  onClick: () =>
                    emitEvent("on:update", {
                      target: "open-group-restrictions-drawer",
                      data: { groupId: group?.id },
                    }),
                });
              }

              if (group?.course?.edhCanEdit && userCanMigrate) {
                menuItems.push({
                  label: t("bundle.link-card.group-table.actions.migrate-group"),
                  icon: "circle-migrate",
                  onClick: () => onMigrateGroup?.(group),
                });
              }

              if (group?.course?.edhCanEdit && userCanDelete) {
                menuItems.push({
                  label: t("bundle.link-card.group-table.actions.delete-group"),
                  icon: "circle-minus",
                  onClick: () => onDeleteGroup?.(group),
                  disabled: group && group.enrollment && group.enrollment > 0,
                });
              }

              return <GroupActionButton menuItems={menuItems} />;
            },
          },
        ]}
      />
    </div>
  );
};

export default GroupsTable;
